import type { HelpArticle } from '../types';
import { categoryAnchorId, normalize } from '../utils';
import { ArticleRow } from './ArticleRow';
import { HelpEmptyState } from './HelpEmptyState';
import { HelpFallbackSupport } from './HelpFallbackSupport';
import { Fragment } from 'react';

interface ArticleListProps {
  articles: HelpArticle[];
  searchTerm: string;
  onOpenArticle: (id: string) => void;
  onContactSupport: () => void;
}

function matchesSearch(article: HelpArticle, term: string): boolean {
  const normalizedTerm = normalize(term.trim());
  if (normalizedTerm === '') return true;
  const haystack = normalize(`${article.title} ${article.snippet} ${article.category}`);
  return haystack.includes(normalizedTerm);
}

export function ArticleList({ articles, searchTerm, onOpenArticle, onContactSupport }: ArticleListProps) {
  const filtered = articles.filter((a) => matchesSearch(a, searchTerm));
  const isGrouped = searchTerm.trim() === '';
  const categories = Array.from(new Set(articles.map((a) => a.category)));

  if (filtered.length === 0) {
    return <HelpEmptyState searchTerm={searchTerm} onContactSupport={onContactSupport} />;
  }

  return (
    <div className="pb-3 pt-1.5">
      {isGrouped
        ? categories.map((category) => {
            const items = filtered.filter((a) => a.category === category);
            if (items.length === 0) return null;
            return (
              <Fragment key={category}>
                <p
                  id={categoryAnchorId(category)}
                  className="mb-2 mt-5 px-2 text-label font-bold uppercase tracking-wide text-subtle first:mt-1"
                >
                  {category}
                </p>
                {items.map((article) => (
                  <ArticleRow key={article.id} article={article} showCategory={false} onOpen={onOpenArticle} />
                ))}
              </Fragment>
            );
          })
        : filtered.map((article) => (
            <ArticleRow key={article.id} article={article} showCategory onOpen={onOpenArticle} />
          ))}

      <HelpFallbackSupport onContactSupport={onContactSupport} />
    </div>
  );
}
